// src/components/admin/PhotoModeration.tsx
//
// Admin photo moderation UI. Two views:
//   - Pending: review queue. Each card has approve/reject buttons + caption edit.
//   - Approved: already-published photos with a "Reject" button for retroactive
//     pulls (the durable .mov fix path).
//
// Mounted as a tab inside /admin/Dashboard. Backend: /api/manage/photos/*.

import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../auth/useAuth";

type PhotoStatus = "pending" | "approved" | "rejected";

interface Photo {
  id:                string;
  eventYear:         string;
  status:            PhotoStatus;
  url?:              string;
  thumbnailUrl?:     string;
  caption?:          string;
  originalFilename?: string;
  uploadedBy?:       string;
  uploadedByName?:   string;
  uploadedAt?:       string;
  batchId?:          string;
}

interface PageResult {
  photos:   Photo[];
  total:    number;
  page:     number;
  pageSize: number;
}

const PAGE_SIZE = 24;

export default function PhotoModeration() {
  const { getApiToken } = useAuth();
  const [view, setView] = useState<"pending" | "approved">("pending");

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <section className="moderation">
      <header className="moderation-header">
        <div>
          <div className="moderation-eyebrow">— Photos</div>
          <h2 className="moderation-title">Photo Moderation</h2>
        </div>
        <div className="moderation-tabs" role="tablist">
          <button
            className={`moderation-tab ${view === "pending" ? "is-active" : ""}`}
            onClick={() => setView("pending")}
            role="tab"
            aria-selected={view === "pending"}
          >
            Pending Review
          </button>
          <button
            className={`moderation-tab ${view === "approved" ? "is-active" : ""}`}
            onClick={() => setView("approved")}
            role="tab"
            aria-selected={view === "approved"}
          >
            Manage Approved
          </button>
        </div>
      </header>

      {view === "pending"
        ? <PendingView getApiToken={getApiToken} />
        : <ApprovedView getApiToken={getApiToken} />}

      <style>{moderationStyles}</style>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Pending review view
// ─────────────────────────────────────────────────────────────────────────────
function PendingView({ getApiToken }: { getApiToken: () => Promise<string | null> }) {
  const [photos,    setPhotos]    = useState<Photo[]>([]);
  const [total,     setTotal]     = useState(0);
  const [page,      setPage]      = useState(0);
  const [loading,   setLoading]   = useState(true);
  const [busy,      setBusy]      = useState<string | null>(null);
  const [editingCaptions, setEditingCaptions] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getApiToken();
      const res = await fetch(`/api/manage/photos?status=pending&page=${page}&pageSize=${PAGE_SIZE}`, {
        headers: { "Authorization": `Bearer ${token}` },
      });
      if (!res.ok) {
        alert(`Failed to load pending photos: ${res.statusText}`);
        return;
      }
      const data = await res.json() as PageResult;
      setPhotos(data.photos);
      setTotal(data.total);

      // Seed edit-state from the existing captions
      const captions: Record<string, string> = {};
      data.photos.forEach(p => { captions[p.id] = p.caption ?? ""; });
      setEditingCaptions(captions);
    } finally {
      setLoading(false);
    }
  }, [getApiToken, page]);

  useEffect(() => { load(); }, [load]);

  async function approve(photo: Photo) {
    if (busy) return;
    setBusy(photo.id);
    try {
      const token = await getApiToken();
      const res = await fetch(`/api/manage/photos/${encodeURIComponent(photo.id)}/approve`, {
        method:  "PUT",
        headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
        body:    JSON.stringify({ caption: editingCaptions[photo.id] ?? "" }),
      });
      if (!res.ok) {
        alert(`Approve failed: ${res.statusText}`);
        return;
      }
      // Remove from view; if last on the page, step back
      setPhotos(prev => prev.filter(p => p.id !== photo.id));
      setTotal(t => Math.max(0, t - 1));
      if (photos.length === 1 && page > 0) setPage(page - 1);
    } finally {
      setBusy(null);
    }
  }

  async function reject(photo: Photo) {
    if (busy) return;
    if (!confirm(`Reject "${photo.originalFilename ?? photo.id}"? It can still be recovered from rejected/ in storage.`)) return;
    setBusy(photo.id);
    try {
      const token = await getApiToken();
      const res = await fetch(`/api/manage/photos/${encodeURIComponent(photo.id)}/reject`, {
        method:  "PUT",
        headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (!res.ok) {
        alert(`Reject failed: ${res.statusText}`);
        return;
      }
      setPhotos(prev => prev.filter(p => p.id !== photo.id));
      setTotal(t => Math.max(0, t - 1));
      if (photos.length === 1 && page > 0) setPage(page - 1);
    } finally {
      setBusy(null);
    }
  }

  async function approveBatch(batchId: string, count: number) {
    if (busy) return;
    if (!confirm(`Approve all ${count} pending photo(s) from this batch?`)) return;
    setBusy(`batch:${batchId}`);
    try {
      const token = await getApiToken();
      const res = await fetch(`/api/manage/photos/batch/${encodeURIComponent(batchId)}/approve`, {
        method:  "PUT",
        headers: { "Authorization": `Bearer ${token}` },
      });
      if (!res.ok) {
        alert(`Batch approve failed: ${res.statusText}`);
        return;
      }
      const result = await res.json();
      if (result.failed > 0) {
        alert(`${result.approved} approved, ${result.failed} failed.`);
      }
      await load();
    } finally {
      setBusy(null);
    }
  }

  if (loading)        return <div className="moderation-status">Loading pending photos…</div>;
  if (photos.length === 0) return <div className="moderation-empty">✓ No photos pending review.</div>;

  // Group photos by batchId for the batch-approve affordance. Same-batch
  // photos appear together in the queue because they share an uploadedAt.
  const batchCounts = new Map<string, number>();
  photos.forEach(p => {
    if (p.batchId) batchCounts.set(p.batchId, (batchCounts.get(p.batchId) ?? 0) + 1);
  });

  return (
    <div>
      <div className="moderation-meta">
        Showing {photos.length} of {total} pending · oldest first
      </div>

      <div className="moderation-grid">
        {photos.map(photo => {
          const batchSize = photo.batchId ? batchCounts.get(photo.batchId) ?? 1 : 1;
          const isFirstOfBatch = photo.batchId
            ? photos.findIndex(p => p.batchId === photo.batchId) === photos.indexOf(photo)
            : false;
          const showBatchButton = batchSize > 1 && isFirstOfBatch;

          return (
            <article key={photo.id} className="moderation-card" data-busy={busy === photo.id}>
              <div className="moderation-thumb">
                {photo.thumbnailUrl || photo.url
                  ? <img src={photo.thumbnailUrl ?? photo.url} alt={photo.caption ?? photo.originalFilename ?? ""} loading="lazy" />
                  : <span>No preview</span>}
              </div>
              <div className="moderation-meta-row">
                <span className="moderation-year">{photo.eventYear}</span>
                <span className="moderation-uploader">
                  {photo.uploadedByName ?? photo.uploadedBy ?? "Unknown"}
                </span>
                {photo.uploadedAt && (
                  <span className="moderation-when">{formatRelative(photo.uploadedAt)}</span>
                )}
              </div>
              <div className="moderation-filename">{photo.originalFilename ?? photo.id}</div>
              <input
                type="text"
                className="moderation-caption"
                placeholder="Caption (optional)"
                value={editingCaptions[photo.id] ?? ""}
                onChange={(e) => setEditingCaptions(prev => ({ ...prev, [photo.id]: e.target.value }))}
                maxLength={250}
                disabled={!!busy}
              />
              <div className="moderation-actions">
                <button
                  className="moderation-approve"
                  onClick={() => approve(photo)}
                  disabled={!!busy}
                >
                  Approve
                </button>
                <button
                  className="moderation-reject"
                  onClick={() => reject(photo)}
                  disabled={!!busy}
                >
                  Reject
                </button>
              </div>
              {showBatchButton && (
                <button
                  className="moderation-batch"
                  onClick={() => approveBatch(photo.batchId!, batchSize)}
                  disabled={!!busy}
                >
                  Approve all {batchSize} from this upload
                </button>
              )}
            </article>
          );
        })}
      </div>

      <Pagination total={total} page={page} pageSize={PAGE_SIZE} onChange={setPage} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Approved photos view (retroactive rejection)
// ─────────────────────────────────────────────────────────────────────────────
function ApprovedView({ getApiToken }: { getApiToken: () => Promise<string | null> }) {
  const [photos,  setPhotos]  = useState<Photo[]>([]);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(0);
  const [year,    setYear]    = useState<string>("");
  const [years,   setYears]   = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy,    setBusy]    = useState<string | null>(null);

  // Fetch available years once
  useEffect(() => {
    (async () => {
      try {
        const token = await getApiToken();
        const res = await fetch("/api/public/years", { headers: { "Authorization": `Bearer ${token}` } });
        if (!res.ok) return;
        const data = await res.json() as { years: string[] };
        setYears(data.years);
        if (data.years.length > 0) setYear(data.years[data.years.length - 1]);
      } catch { /* shrug */ }
    })();
  }, [getApiToken]);

  const load = useCallback(async () => {
    if (!year) return;
    setLoading(true);
    try {
      const token = await getApiToken();
      const res = await fetch(
        `/api/manage/photos?status=approved&year=${year}&page=${page}&pageSize=${PAGE_SIZE}`,
        { headers: { "Authorization": `Bearer ${token}` } }
      );
      if (!res.ok) { alert(`Failed to load: ${res.statusText}`); return; }
      const data = await res.json() as PageResult;
      setPhotos(data.photos);
      setTotal(data.total);
    } finally {
      setLoading(false);
    }
  }, [getApiToken, year, page]);

  useEffect(() => { load(); }, [load]);

  // Reset to page 0 whenever year changes
  useEffect(() => { setPage(0); }, [year]);

  async function reject(photo: Photo) {
    if (busy) return;
    if (!confirm(`Pull "${photo.caption ?? photo.originalFilename ?? photo.id}" from the gallery? It can be recovered from rejected/ in storage.`)) return;
    setBusy(photo.id);
    try {
      const token = await getApiToken();
      const res = await fetch(`/api/manage/photos/${encodeURIComponent(photo.id)}/reject`, {
        method:  "PUT",
        headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (!res.ok) { alert(`Reject failed: ${res.statusText}`); return; }
      setPhotos(prev => prev.filter(p => p.id !== photo.id));
      setTotal(t => Math.max(0, t - 1));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div>
      <div className="moderation-controls">
        <label className="moderation-year-picker">
          <span>Year</span>
          <select value={year} onChange={(e) => setYear(e.target.value)}>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </label>
        <span className="moderation-meta">
          {loading ? "Loading…" : `${total} approved photo${total === 1 ? "" : "s"}`}
        </span>
      </div>

      {!loading && photos.length === 0 && (
        <div className="moderation-empty">No approved photos for {year}.</div>
      )}

      {photos.length > 0 && (
        <div className="moderation-grid moderation-grid-compact">
          {photos.map(photo => (
            <article key={photo.id} className="moderation-card moderation-card-compact" data-busy={busy === photo.id}>
              <div className="moderation-thumb">
                {photo.thumbnailUrl || photo.url
                  ? <img src={photo.thumbnailUrl ?? photo.url} alt={photo.caption ?? ""} loading="lazy" />
                  : <span>No preview</span>}
              </div>
              {photo.caption && <div className="moderation-caption-display">{photo.caption}</div>}
              <div className="moderation-filename">{photo.originalFilename ?? photo.id}</div>
              <button
                className="moderation-reject"
                onClick={() => reject(photo)}
                disabled={!!busy}
              >
                Pull from gallery
              </button>
            </article>
          ))}
        </div>
      )}

      <Pagination total={total} page={page} pageSize={PAGE_SIZE} onChange={setPage} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Pagination — page-numbered, matching Gallery.tsx pattern
// ─────────────────────────────────────────────────────────────────────────────
function Pagination({ total, page, pageSize, onChange }: {
  total: number; page: number; pageSize: number; onChange: (p: number) => void;
}) {
  const lastPage = Math.max(0, Math.ceil(total / pageSize) - 1);
  if (lastPage === 0) return null;

  const items: (number | "...")[] = [];
  const push = (x: number | "...") => items[items.length - 1] !== x && items.push(x);
  push(0);
  if (page - 1 > 1) push("...");
  for (let i = Math.max(1, page - 1); i <= Math.min(lastPage - 1, page + 1); i++) push(i);
  if (page + 1 < lastPage - 1) push("...");
  if (lastPage > 0) push(lastPage);

  return (
    <nav className="moderation-pagination">
      <button onClick={() => onChange(Math.max(0, page - 1))} disabled={page === 0}>‹ Prev</button>
      {items.map((it, i) => it === "..."
        ? <span key={`g${i}`} className="pagination-gap">…</span>
        : <button
            key={it}
            onClick={() => onChange(it)}
            className={it === page ? "is-current" : ""}
          >{it + 1}</button>)}
      <button onClick={() => onChange(Math.min(lastPage, page + 1))} disabled={page === lastPage}>Next ›</button>
    </nav>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const now  = Date.now();
  const diff = Math.floor((now - then) / 1000);
  if (diff < 60)         return "just now";
  if (diff < 3600)       return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400)      return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7)  return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

// ─────────────────────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────────────────────
const moderationStyles = `
  .moderation { }
  .moderation-header {
    display: flex; flex-wrap: wrap; gap: 1.25rem; align-items: center; justify-content: space-between;
    margin-bottom: 1.5rem;
  }
  .moderation-eyebrow {
    font-family: var(--font-display); text-transform: uppercase;
    letter-spacing: 0.35em; color: var(--red); font-size: 0.7rem; font-weight: 500;
  }
  .moderation-title {
    font-family: var(--font-display); text-transform: uppercase;
    font-weight: 700; font-size: 1.6rem; margin: 0.25rem 0 0; letter-spacing: 0.02em;
  }
  .moderation-tabs {
    display: flex; gap: 0.5rem; flex-wrap: wrap;
  }
  .moderation-tab {
    background: white; border: 1px solid var(--border); border-radius: var(--radius);
    padding: 0.6rem 1.1rem; font-family: var(--font-display);
    text-transform: uppercase; letter-spacing: 0.06em; font-size: 0.8rem; font-weight: 500;
    color: var(--mid); cursor: pointer; min-height: 40px;
  }
  .moderation-tab:hover, .moderation-tab:focus { color: var(--text); border-color: var(--mid); }
  .moderation-tab.is-active {
    background: var(--red); color: white; border-color: var(--red);
  }

  .moderation-status, .moderation-empty {
    padding: 2rem; text-align: center; color: var(--mid);
    background: var(--bg); border-radius: var(--radius);
  }
  .moderation-empty { color: #2e7d32; font-weight: 500; }

  .moderation-meta {
    color: var(--mid); font-size: 0.9rem; margin-bottom: 1rem;
  }

  .moderation-controls {
    display: flex; flex-wrap: wrap; gap: 1rem 2rem; align-items: center;
    margin-bottom: 1.25rem;
  }
  .moderation-year-picker {
    display: flex; align-items: center; gap: 0.6rem;
    font-family: var(--font-display); text-transform: uppercase;
    font-size: 0.8rem; letter-spacing: 0.06em; font-weight: 500;
  }
  .moderation-year-picker select {
    font-family: var(--font-sub); font-size: 1rem;
    padding: 0.5rem 0.75rem; border: 1px solid var(--border);
    border-radius: var(--radius); background: white; min-height: 40px;
  }

  .moderation-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 1.25rem;
  }
  .moderation-grid-compact {
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 1rem;
  }

  .moderation-card {
    background: white;
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 0.85rem;
    display: flex; flex-direction: column; gap: 0.5rem;
    transition: opacity 0.15s;
  }
  .moderation-card[data-busy="true"] { opacity: 0.5; pointer-events: none; }
  .moderation-card-compact { padding: 0.6rem; gap: 0.4rem; }

  .moderation-thumb {
    width: 100%; aspect-ratio: 4/3; background: var(--bg);
    border-radius: var(--radius); overflow: hidden;
    display: flex; align-items: center; justify-content: center;
    color: var(--muted); font-size: 0.85rem;
  }
  .moderation-thumb img { width: 100%; height: 100%; object-fit: cover; }

  .moderation-meta-row {
    display: flex; flex-wrap: wrap; gap: 0.4rem 0.65rem;
    font-size: 0.75rem; color: var(--mid);
    align-items: center;
  }
  .moderation-year {
    font-family: var(--font-display); text-transform: uppercase;
    font-weight: 600; color: var(--red); letter-spacing: 0.06em;
  }
  .moderation-uploader { font-weight: 500; color: var(--text); }
  .moderation-when { color: var(--muted); }

  .moderation-filename {
    font-size: 0.8rem; color: var(--muted);
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .moderation-caption-display {
    font-size: 0.9rem; color: var(--text); font-weight: 500;
    overflow: hidden; display: -webkit-box;
    -webkit-line-clamp: 2; -webkit-box-orient: vertical;
  }

  .moderation-caption {
    width: 100%; box-sizing: border-box;
    font-family: var(--font-sub); font-size: 0.9rem;
    padding: 0.5rem 0.65rem;
    border: 1px solid var(--border);
    border-radius: var(--radius);
    background: var(--bg); color: var(--text);
  }
  .moderation-caption:focus {
    outline: none; border-color: var(--mid); background: white;
  }
  .moderation-caption::placeholder { color: var(--muted); }

  .moderation-actions {
    display: flex; gap: 0.5rem;
  }
  .moderation-approve, .moderation-reject {
    flex: 1;
    font-family: var(--font-display);
    text-transform: uppercase; letter-spacing: 0.06em;
    font-size: 0.8rem; font-weight: 600;
    padding: 0.7rem 0.9rem;
    border-radius: var(--radius);
    cursor: pointer;
    min-height: 40px;
    transition: background 0.15s, border-color 0.15s;
  }
  .moderation-approve {
    background: var(--red); color: white; border: 1px solid var(--red);
  }
  .moderation-approve:hover:not(:disabled) { background: var(--red-dark); border-color: var(--red-dark); }
  .moderation-approve:disabled { background: var(--mid); border-color: var(--mid); cursor: not-allowed; }
  .moderation-reject {
    background: white; color: var(--text); border: 1px solid var(--border);
  }
  .moderation-reject:hover:not(:disabled) { color: var(--red); border-color: var(--red); }
  .moderation-reject:disabled { color: var(--muted); cursor: not-allowed; }

  .moderation-batch {
    background: var(--bg);
    border: 1px dashed var(--border);
    border-radius: var(--radius);
    padding: 0.55rem 0.8rem;
    font-family: var(--font-display);
    text-transform: uppercase; letter-spacing: 0.06em;
    font-size: 0.7rem; color: var(--mid); cursor: pointer;
    margin-top: 0.25rem;
  }
  .moderation-batch:hover:not(:disabled) {
    color: var(--red); border-color: var(--red);
  }
  .moderation-batch:disabled { opacity: 0.5; cursor: not-allowed; }

  .moderation-pagination {
    display: flex; justify-content: center; align-items: center;
    gap: 0.4rem; flex-wrap: wrap;
    margin-top: 1.5rem;
  }
  .moderation-pagination button {
    background: white; border: 1px solid var(--border); border-radius: var(--radius);
    padding: 0.5rem 0.9rem; min-width: 40px; min-height: 40px;
    font-family: var(--font-sub); font-size: 0.9rem;
    color: var(--text); cursor: pointer;
  }
  .moderation-pagination button:disabled { color: var(--muted); cursor: not-allowed; }
  .moderation-pagination button.is-current {
    background: var(--red); color: white; border-color: var(--red);
  }
  .pagination-gap { padding: 0 0.4rem; color: var(--muted); }

  @media (max-width: 640px) {
    .moderation-grid { grid-template-columns: 1fr; }
    .moderation-grid-compact { grid-template-columns: repeat(2, 1fr); }
    .moderation-tabs { width: 100%; }
    .moderation-tab { flex: 1; text-align: center; }
  }
`;
